// pages/EncuestasListado.js
import React, { useState, useEffect, useMemo } from 'react';
import {
  Table, Button, Modal, Space, Typography, message, Tag, Tooltip,
  Row, Col, Popconfirm, Badge, Statistic, Card, Rate, Descriptions,
  DatePicker, Divider, Empty, Input, Segmented, Grid, Progress, Avatar
} from 'antd';
import {
  SearchOutlined, ReloadOutlined, EyeOutlined, DeleteOutlined,
  CheckCircleOutlined, CloseCircleOutlined, StarOutlined,
  FileTextOutlined, UserOutlined, BarChartOutlined, GlobalOutlined,
  RiseOutlined, PieChartOutlined, TeamOutlined, FundOutlined,
  SmileOutlined, FrownOutlined, MehOutlined, TrophyOutlined,
  FireOutlined, CalendarOutlined, ClearOutlined, HeartOutlined,
  ExperimentOutlined, PercentageOutlined
} from '@ant-design/icons';
import {
  ResponsiveContainer, BarChart, Bar, LineChart, Line, PieChart, Pie,
  Cell, XAxis, YAxis, CartesianGrid, Tooltip as RTooltip, Legend,
  ComposedChart, AreaChart, Area, RadarChart, PolarGrid,
  PolarAngleAxis, PolarRadiusAxis, Radar
} from 'recharts';
import axios from 'axios';
import dayjs from '../utils/dayjsConfig';

import PageContainer from '../components/PageContainer';
import { BASE_URL } from '../utils/constants';

const { Text } = Typography;
const { RangePicker } = DatePicker;
const { useBreakpoint } = Grid;

// 🎨 Paleta premium
const COLORS = [
  '#1890ff', '#52c41a', '#faad14', '#ff4d4f', '#722ed1',
  '#13c2c2', '#eb2f96', '#fa8c16', '#a0d911', '#2f54eb'
];

// Colores por valoración
const VALORACION_COLORS = {
  EXCELENTE: '#52c41a',
  BIEN: '#1890ff',
  REGULAR: '#faad14',
  MAL: '#ff4d4f',
};

const VALORACION_LABEL = {
  EXCELENTE: 'Excelente',
  BIEN: 'Bien',
  REGULAR: 'Regular',
  MAL: 'Mal',
};

const valoracionColor = (v) => {
  switch (v) {
    case 'EXCELENTE': return 'green';
    case 'BIEN': return 'blue';
    case 'REGULAR': return 'orange';
    case 'MAL': return 'red';
    default: return 'default';
  }
};

const KPICard = ({ title, value, icon, color = '#1890ff', subtitle, isMobile }) => (
  <Card
    bordered={false}
    styles={{ body: { padding: isMobile ? 10 : 12 } }}
    style={{
      background: `linear-gradient(135deg, ${color}15 0%, ${color}05 100%)`,
      borderLeft: `3px solid ${color}`,
      borderRadius: 10,
      height: '100%',
      position: 'relative',
      overflow: 'hidden',
      minHeight: isMobile ? 70 : 78,
    }}
    hoverable
  >
    <div style={{
      position: 'absolute', right: -10, top: -10, opacity: 0.07,
      fontSize: isMobile ? 50 : 60, color, pointerEvents: 'none',
    }}>
      {icon}
    </div>

    <Space direction="vertical" size={2} style={{ width: '100%', position: 'relative', zIndex: 1 }}>
      <Space size={4}>
        <span style={{ color, fontSize: isMobile ? 11 : 12 }}>{icon}</span>
        <Text type="secondary" style={{
          fontSize: isMobile ? 9 : 10, textTransform: 'uppercase',
          letterSpacing: 0.3, fontWeight: 600,
          whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
        }}>
          {title}
        </Text>
      </Space>

      <span style={{
        fontSize: isMobile ? 15 : 18,
        fontWeight: 700, color: '#1f1f1f', lineHeight: 1.1,
        whiteSpace: 'nowrap',
      }}>
        {value}
      </span>

      {subtitle && (
        <Text type="secondary" style={{
          fontSize: isMobile ? 9 : 10,
          whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
          display: 'block',
        }}>
          {subtitle}
        </Text>
      )}
    </Space>
  </Card>
);

const EncuestasListado = () => {
  const screens = useBreakpoint();
  const isMobile = !screens.md;

  // Estados principales
  const [respuestas, setRespuestas] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchText, setSearchText] = useState('');
  const [rangoFechas, setRangoFechas] = useState(null);
  const [searchLoading, setSearchLoading] = useState(false);
  const [estadisticas, setEstadisticas] = useState(null);
  const [filteredInfo, setFilteredInfo] = useState({});
  const [sortedInfo, setSortedInfo] = useState({});
  const [detalleVisible, setDetalleVisible] = useState(false);
  const [detalle, setDetalle] = useState(null);
  const [loadingDetalle, setLoadingDetalle] = useState(false);
  const [tipoGraficoPreguntas, setTipoGraficoPreguntas] = useState('bar');

  // ===== Columnas de tabla =====
  const columns = useMemo(() => [
    {
      title: 'ID',
      dataIndex: 'Id',
      key: 'Id',
      width: 70,
      sorter: (a, b) => a.Id - b.Id,
      sortOrder: sortedInfo.columnKey === 'Id' && sortedInfo.order,
      render: (id) => <Tag color="default">#{id}</Tag>,
      fixed: isMobile ? false : 'left',
    },
    {
      title: 'Fecha',
      dataIndex: 'FechaEncuesta',
      key: 'FechaEncuesta',
      width: 160,
      sorter: (a, b) => new Date(a.FechaEncuesta) - new Date(b.FechaEncuesta),
      sortOrder: sortedInfo.columnKey === 'FechaEncuesta' && sortedInfo.order,
      render: (f) => (
        <Space>
          <CalendarOutlined style={{ color: '#1890ff' }} />
          <Text>{f ? dayjs(f).format('DD/MM/YYYY') : '-'}</Text>
        </Space>
      ),
    },
    {
      title: 'Habitación',
      dataIndex: 'NumHabitacion',
      key: 'NumHabitacion',
      width: 110,
      sorter: (a, b) => (a.NumHabitacion || '').localeCompare(b.NumHabitacion || ''),
      sortOrder: sortedInfo.columnKey === 'NumHabitacion' && sortedInfo.order,
      render: (h) => h ? <Tag color="blue" style={{ fontWeight: 600 }}>{h}</Tag> : '-',
    },
    {
      title: 'País',
      dataIndex: 'PaisProcedencia',
      key: 'PaisProcedencia',
      width: 140,
      sorter: (a, b) => (a.PaisProcedencia || '').localeCompare(b.PaisProcedencia || ''),
      sortOrder: sortedInfo.columnKey === 'PaisProcedencia' && sortedInfo.order,
      render: (p) => p ? (
        <Space><GlobalOutlined style={{ color: '#13c2c2' }} />{p}</Space>
      ) : <Text type="secondary">-</Text>,
    },
    {
      title: 'Edad',
      dataIndex: 'Edad',
      key: 'Edad',
      width: 80,
      sorter: (a, b) => (a.Edad || 0) - (b.Edad || 0),
      sortOrder: sortedInfo.columnKey === 'Edad' && sortedInfo.order,
      render: (e) => e ? <Tag color="purple">{e}</Tag> : '-',
    },
    {
      title: 'Media valoración',
      dataIndex: 'MediaValoracion',
      key: 'MediaValoracion',
      width: 180,
      sorter: (a, b) => (a.MediaValoracion || 0) - (b.MediaValoracion || 0),
      sortOrder: sortedInfo.columnKey === 'MediaValoracion' && sortedInfo.order,
      render: (m) => {
        if (!m) return <Text type="secondary">-</Text>;
        const val = parseFloat(m);
        const color = val >= 3.5 ? '#52c41a' : val >= 2.5 ? '#1890ff' : val >= 1.5 ? '#faad14' : '#ff4d4f';
        return (
          <Space>
            <Rate disabled allowHalf defaultValue={val} count={4} style={{ fontSize: 14, color }} />
            <Text strong style={{ color }}>{val.toFixed(2)}/4</Text>
          </Space>
        );
      },
    },
    {
      title: 'Repetidor',
      dataIndex: 'HaEstadoAntes',
      key: 'HaEstadoAntes',
      width: 110,
      render: (v) => v
        ? <Badge status="success" text="Sí" />
        : <Badge status="default" text="No" />,
      filters: [{ text: 'Sí', value: true }, { text: 'No', value: false }],
      filteredValue: filteredInfo.HaEstadoAntes || null,
      onFilter: (value, record) => record.HaEstadoAntes === value,
    },
    {
      title: 'Volvería',
      dataIndex: 'LeGustariaVolver',
      key: 'LeGustariaVolver',
      width: 100,
      render: (v) => v
        ? <CheckCircleOutlined style={{ color: '#52c41a', fontSize: 18 }} />
        : <CloseCircleOutlined style={{ color: '#ff4d4f', fontSize: 18 }} />,
      filters: [{ text: 'Sí', value: true }, { text: 'No', value: false }],
      filteredValue: filteredInfo.LeGustariaVolver || null,
      onFilter: (value, record) => record.LeGustariaVolver === value,
    },
    {
      title: 'Acciones',
      key: 'actions',
      width: 150,
      fixed: isMobile ? false : 'right',
      render: (_, record) => (
        <Space>
          <Tooltip title="Ver detalle">
            <Button type="default" icon={<EyeOutlined />} size="small"
              onClick={() => handleVerDetalle(record.Id)} />
          </Tooltip>
          <Popconfirm
            title="¿Eliminar encuesta?"
            onConfirm={() => handleDelete(record.Id)}
            okText="Sí" cancelText="No"
            okButtonProps={{ danger: true }}
          >
            <Tooltip title="Eliminar">
              <Button danger icon={<DeleteOutlined />} size="small" />
            </Tooltip>
          </Popconfirm>
        </Space>
      ),
    },
  ], [sortedInfo, filteredInfo, isMobile]);

  // ===== Cargas =====
  useEffect(() => {
    fetchRespuestas();
    fetchEstadisticas();
  }, []);

  const fetchRespuestas = async (params = {}) => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const queryParams = {
        search: searchText,
        sortField: params.sortField || sortedInfo.field || 'FechaEncuesta',
        sortOrder: params.sortOrder || (sortedInfo.order === 'ascend' ? 'ASC' : 'DESC')
      };
      if (rangoFechas && rangoFechas[0] && rangoFechas[1]) {
        queryParams.fechaDesde = rangoFechas[0].format('YYYY-MM-DD');
        queryParams.fechaHasta = rangoFechas[1].format('YYYY-MM-DD');
      }

      console.log(" params => ",queryParams);

      
      const response = await axios.get(`${BASE_URL}/api/encuestas/respuestas`, {
        headers: { Authorization: `Bearer ${token}` },
        params: queryParams
      });
      setRespuestas(response.data.data || []);
    } catch (error) {
      console.error('❌ Error cargando respuestas:', error);
      message.error('Error al cargar las encuestas');
      setRespuestas([]);
    } finally {
      setLoading(false);
      setSearchLoading(false);
    }
  };

    const fetchEstadisticas = async () => {
    try {
        const token = localStorage.getItem('token');

        // 🎯 Construir params con los filtros activos
        const queryParams = {};

        if (rangoFechas && rangoFechas[0] && rangoFechas[1]) {
        queryParams.fechaDesde = rangoFechas[0].format('YYYY-MM-DD');
        queryParams.fechaHasta = rangoFechas[1].format('YYYY-MM-DD');
        }

        if (searchText && searchText.trim() !== '') {
        queryParams.search = searchText.trim();
        }

        const response = await axios.get(`${BASE_URL}/api/encuestas/estadisticas`, {
        headers: { Authorization: `Bearer ${token}` },
        params: queryParams,
        });
        setEstadisticas(response.data.data);
    } catch (error) {
        console.error('❌ Error cargando estadísticas:', error);
        message.error('Error al cargar estadísticas');
    }
    };

  const handleVerDetalle = async (id) => {
    setLoadingDetalle(true);
    setDetalleVisible(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${BASE_URL}/api/encuestas/respuestas/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setDetalle(response.data.data);
    } catch (error) {
      message.error('Error al cargar el detalle');
    } finally {
      setLoadingDetalle(false);
    }
  };

  const handleDelete = async (id) => {
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${BASE_URL}/api/encuestas/respuestas/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setRespuestas(respuestas.filter(r => r.Id !== id));
      message.success('Encuesta eliminada');
      fetchEstadisticas();
    } catch (error) {
      message.error(error.response?.data?.error || 'Error al eliminar');
    }
  };

    const handleSearch = () => {
        setSearchLoading(true);
        fetchRespuestas();      
        fetchEstadisticas();    
    };

    const handleResetSearch = () => {
        setSearchText('');
        setRangoFechas(null);
        setFilteredInfo({});
        setSortedInfo({});
        setSearchLoading(false);
        fetchRespuestas();
        fetchEstadisticas();    
    };

  // ===== KPIs =====
  const kpis = useMemo(() => {
    if (!estadisticas?.resumen) return {
      total: 0, valoracionMedia: 0, repetidores: 0, volverian: 0,
      edadMedia: 0, totalPaises: 0, tasaVolver: 0, tasaRepetir: 0,
    };
    const r = estadisticas.resumen;
    const total = r.TotalEncuestas || 0;
    return {
      total,
      valoracionMedia: parseFloat(r.ValoracionMedia || 0),
      repetidores: r.Repetidores || 0,
      volverian: r.Volverian || 0,
      edadMedia: parseFloat(r.EdadMedia || 0),
      totalPaises: r.TotalPaises || 0,
      tasaVolver: total > 0 ? (r.Volverian / total) * 100 : 0,
      tasaRepetir: total > 0 ? (r.Repetidores / total) * 100 : 0,
    };
  }, [estadisticas]);

  // ===== Datos para gráficos =====
  const dataDistribucionValoracion = useMemo(() =>
    (estadisticas?.distribucion || []).map(d => ({
      name: VALORACION_LABEL[d.Valoracion] || d.Valoracion,
      key: d.Valoracion,
      value: Number(d.Total) || 0,
      color: VALORACION_COLORS[d.Valoracion] || '#d9d9d9',
    })), [estadisticas]);

    const dataPorPregunta = useMemo(() =>
    (estadisticas?.porPregunta || [])
        .filter(p => p.Media !== null && p.Media !== undefined)
        .map(p => {
        // Texto combinado: "[Categoría] Pregunta"
        const textoCompleto = p.CategoriaDescripcion
            ? `[${p.CategoriaDescripcion}] ${p.PreguntaTexto || ''}`
            : (p.PreguntaTexto || `Pregunta #${p.Id}`);

        // Versión corta para el eje Y (truncada)
        const textoCorto = textoCompleto.length > 55
            ? textoCompleto.substring(0, 55) + '...'
            : textoCompleto;

        return {
            pregunta: textoCorto,           // ← lo que se muestra en el eje
            preguntaFull: textoCompleto,    // ← lo que sale en el tooltip
            categoria: p.CategoriaDescripcion,
            media: Number(Number(p.Media).toFixed(2)) || 0,
            total: Number(p.Total) || 0,
        };
        }), [estadisticas]);

  const dataPorCategoria = useMemo(() =>
    (estadisticas?.porCategoria || []).map(c => ({
      name: c.CategoriaDescripcion || c.Codigo || 'Sin categoría',
      media: Number(Number(c.Media).toFixed(2)) || 0,
      total: Number(c.Total) || 0,
      // Para radar necesitamos escala 0-4
      value: Number(Number(c.Media).toFixed(2)) || 0,
    })), [estadisticas]);

  const dataPorPais = useMemo(() =>
    (estadisticas?.porPais || []).slice(0, 10).map(p => ({
      name: p.PaisProcedencia || 'Sin especificar',
      value: Number(p.Total) || 0,
    })), [estadisticas]);

  const dataPorEdad = useMemo(() =>
    (estadisticas?.porEdad || []).map(e => ({
      name: e.rango || 'Sin datos',
      value: Number(e.total) || 0,
    })), [estadisticas]);

  // ===== Panel de filtros =====
  const filtrosBar = (
    <Card
      size="small"
      style={{
        marginBottom: 16,
        borderRadius: 12,
        background: 'linear-gradient(135deg, #f0f5ff 0%, #ffffff 100%)',
        border: '1px solid #e6f0ff',
        boxShadow: '0 2px 8px rgba(24,144,255,0.06)',
      }}
      styles={{ body: { padding: isMobile ? 12 : 16 } }}
    >
      <Row gutter={[12, 12]} align="middle">
        <Col xs={24} md={10} lg={8}>
          <Input
            placeholder="Buscar por habitación, país, observaciones..."
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            onPressEnter={handleSearch}
            allowClear
            onClear={handleResetSearch}
            prefix={<SearchOutlined style={{ color: '#1890ff' }} />}
          />
        </Col>
        <Col xs={24} sm={14} md={8} lg={7}>
          <RangePicker
            style={{ width: '100%' }}
            value={rangoFechas}
            onChange={setRangoFechas}
            format="DD/MM/YYYY"
            placeholder={['Desde', 'Hasta']}
          />
        </Col>
        <Col xs={24} sm={10} md={6} lg={9}>
          <Space style={{ width: '100%', justifyContent: isMobile ? 'flex-start' : 'flex-end' }}>
            <Button type="primary" icon={<SearchOutlined />}
              onClick={handleSearch} loading={searchLoading}>
              Buscar
            </Button>
            <Button icon={<ReloadOutlined />} onClick={handleResetSearch}>
              Restablecer
            </Button>
            <Tooltip title="Recargar estadísticas">
              <Button icon={<FundOutlined />} onClick={fetchEstadisticas} loading={loading} />
            </Tooltip>
          </Space>
        </Col>
      </Row>
    </Card>
  );

  // ===== Render =====
  return (
    <>
      <PageContainer
        title="Encuestas Realizadas"
        subtitle={`${respuestas.length} encuestas · Valoración media ${kpis.valoracionMedia.toFixed(2)}/4`}
        icon={<FundOutlined />}
        loading={loading}
        onRefresh={() => { fetchRespuestas(); fetchEstadisticas(); }}
        badgeCount={respuestas.length}
        badgeStatus="processing"
        cardStyle={{ borderRadius: 12, boxShadow: '0 4px 12px rgba(0,0,0,0.08)' }}
      >
        {filtrosBar}

        {/* ========== KPIs ========== */}
        <Row gutter={[12, 12]} style={{ marginBottom: 16 }}>
          <Col xs={12} sm={12} md={6}>
            <KPICard
              title="Total Encuestas"
              value={kpis.total}
              icon={<FileTextOutlined />}
              color="#1890ff"
              subtitle="Cuestionarios completados"
              isMobile={isMobile}
            />
          </Col>
          <Col xs={12} sm={12} md={6}>
            <KPICard
              title="Valoración Media"
              value={`${kpis.valoracionMedia.toFixed(2)}/4`}
              icon={<StarOutlined />}
              color="#faad14"
              subtitle="Media global"
              isMobile={isMobile}
            />
          </Col>
          <Col xs={12} sm={12} md={6}>
            <KPICard
              title="Volverían"
              value={`${kpis.tasaVolver.toFixed(1)}%`}
              icon={<HeartOutlined />}
              color="#52c41a"
              subtitle={`${kpis.volverian} de ${kpis.total}`}
              isMobile={isMobile}
            />
          </Col>
          <Col xs={12} sm={12} md={6}>
            <KPICard
              title="Repetidores"
              value={`${kpis.tasaRepetir.toFixed(1)}%`}
              icon={<UserOutlined />}
              color="#722ed1"
              subtitle={`${kpis.repetidores} de ${kpis.total}`}
              isMobile={isMobile}
            />
          </Col>
        </Row>

        <Row gutter={[12, 12]} style={{ marginBottom: 16 }}>
          <Col xs={12} sm={8} md={8}>
            <KPICard
              title="Edad Media"
              value={`${kpis.edadMedia.toFixed(0)} años`}
              icon={<TeamOutlined />}
              color="#13c2c2"
              subtitle="De los encuestados"
              isMobile={isMobile}
            />
          </Col>
          <Col xs={12} sm={8} md={8}>
            <KPICard
              title="Países"
              value={kpis.totalPaises}
              icon={<GlobalOutlined />}
              color="#eb2f96"
              subtitle="Diferentes procedencias"
              isMobile={isMobile}
            />
          </Col>
          <Col xs={24} sm={8} md={8}>
            <KPICard
              title="Satisfacción"
              value={`${((kpis.valoracionMedia / 4) * 100).toFixed(0)}%`}
              icon={<PercentageOutlined />}
              color="#fa8c16"
              subtitle="Índice de satisfacción"
              isMobile={isMobile}
            />
          </Col>
        </Row>

        {/* ========== TOP CARDS ========== */}
        <Row gutter={[12, 12]} style={{ marginBottom: 16 }}>
          <Col xs={24} md={8}>
            <Card size="small" style={{ borderRadius: 10, borderLeft: '4px solid #52c41a' }}
              styles={{ body: { padding: 14 } }}>
              <Space>
                <Avatar style={{ backgroundColor: '#52c41a' }} icon={<TrophyOutlined />} />
                <div style={{ flex: 1, minWidth: 0 }}>
                  <Text type="secondary" style={{ fontSize: 11, textTransform: 'uppercase' }}>
                    Mejor Categoría
                  </Text>
                  <div style={{ fontWeight: 700, fontSize: 14, overflow: 'hidden',
                    textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {dataPorCategoria.length > 0
                      ? [...dataPorCategoria].sort((a, b) => b.media - a.media)[0].name
                      : '-'}
                  </div>
                  <Text strong style={{ color: '#52c41a' }}>
                    {dataPorCategoria.length > 0
                      ? `${[...dataPorCategoria].sort((a, b) => b.media - a.media)[0].media.toFixed(2)}/4`
                      : '-'}
                  </Text>
                </div>
              </Space>
            </Card>
          </Col>
          <Col xs={24} md={8}>
            <Card size="small" style={{ borderRadius: 10, borderLeft: '4px solid #ff4d4f' }}
              styles={{ body: { padding: 14 } }}>
              <Space>
                <Avatar style={{ backgroundColor: '#ff4d4f' }} icon={<FrownOutlined />} />
                <div style={{ flex: 1, minWidth: 0 }}>
                  <Text type="secondary" style={{ fontSize: 11, textTransform: 'uppercase' }}>
                    Peor Categoría
                  </Text>
                  <div style={{ fontWeight: 700, fontSize: 14, overflow: 'hidden',
                    textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {dataPorCategoria.length > 0
                      ? [...dataPorCategoria].sort((a, b) => a.media - b.media)[0].name
                      : '-'}
                  </div>
                  <Text strong style={{ color: '#ff4d4f' }}>
                    {dataPorCategoria.length > 0
                      ? `${[...dataPorCategoria].sort((a, b) => a.media - b.media)[0].media.toFixed(2)}/4`
                      : '-'}
                  </Text>
                </div>
              </Space>
            </Card>
          </Col>
          <Col xs={24} md={8}>
            <Card size="small" style={{ borderRadius: 10, borderLeft: '4px solid #faad14' }}
              styles={{ body: { padding: 14 } }}>
              <Space>
                <Avatar style={{ backgroundColor: '#faad14' }} icon={<FireOutlined />} />
                <div style={{ flex: 1, minWidth: 0 }}>
                  <Text type="secondary" style={{ fontSize: 11, textTransform: 'uppercase' }}>
                    Top País
                  </Text>
                  <div style={{ fontWeight: 700, fontSize: 14, overflow: 'hidden',
                    textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {dataPorPais.length > 0 ? dataPorPais[0].name : '-'}
                  </div>
                  <Text strong style={{ color: '#faad14' }}>
                    {dataPorPais.length > 0 ? `${dataPorPais[0].value} encuestas` : '-'}
                  </Text>
                </div>
              </Space>
            </Card>
          </Col>
        </Row>

        {/* ========== FILA 1: Pie Valoración + Columnas Categoría ========== */}
        <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
          <Col xs={24} lg={10}>
            <Card
              title={<Space><PieChartOutlined style={{ color: '#eb2f96' }} /> Distribución por Valoración</Space>}
              size="small"
              style={{ borderRadius: 12 }}
              styles={{ body: { padding: isMobile ? 8 : 16 } }}
            >
              {dataDistribucionValoracion.length > 0 ? (
                <ResponsiveContainer width="100%" height={isMobile ? 240 : 300}>
                  <PieChart>
                    <Pie
                      data={dataDistribucionValoracion}
                      dataKey="value"
                      nameKey="name"
                      cx="50%" cy="50%"
                      innerRadius={isMobile ? 45 : 60}
                      outerRadius={isMobile ? 80 : 100}
                      paddingAngle={4}
                      label={({ name, percent }) =>
                        `${name} (${(percent * 100).toFixed(0)}%)`}
                      labelLine={{ stroke: '#d9d9d9' }}
                    >
                      {dataDistribucionValoracion.map((entry, i) => (
                        <Cell key={i} fill={entry.color} />
                      ))}
                    </Pie>
                    <RTooltip
                      contentStyle={{ borderRadius: 8, border: 'none',
                        boxShadow: '0 4px 12px rgba(0,0,0,0.15)' }}
                      formatter={(v) => [`${v} respuestas`, 'Cantidad']}
                    />
                    <Legend wrapperStyle={{ fontSize: 12 }} />
                  </PieChart>
                </ResponsiveContainer>
              ) : <Empty description="Sin datos" />}
            </Card>
          </Col>

          <Col xs={24} lg={14}>
            <Card
              title={<Space><BarChartOutlined style={{ color: '#1890ff' }} /> Valoración Media por Categoría</Space>}
              size="small"
              style={{ borderRadius: 12 }}
              styles={{ body: { padding: isMobile ? 8 : 16 } }}
            >
              {dataPorCategoria.length > 0 ? (
                <ResponsiveContainer width="100%" height={isMobile ? 240 : 300}>
                  <BarChart data={dataPorCategoria} margin={{ top: 20, right: 20, left: 0, bottom: 5 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                    <XAxis dataKey="name" tick={{ fontSize: 11 }} />
                    <YAxis domain={[0, 4]} tick={{ fontSize: 11 }} tickFormatter={(v) => `${v}`} />
                    <RTooltip
                      contentStyle={{ borderRadius: 8, border: 'none',
                        boxShadow: '0 4px 12px rgba(0,0,0,0.15)' }}
                      formatter={(v) => [`${Number(v).toFixed(2)}/4`, 'Media']}
                    />
                    <Bar dataKey="media" radius={[6, 6, 0, 0]} label={{ position: 'top', fontSize: 11 }}>
                      {dataPorCategoria.map((entry, i) => (
                        <Cell key={i} fill={COLORS[i % COLORS.length]} />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              ) : <Empty description="Sin datos" />}
            </Card>
          </Col>
        </Row>

        {/* ========== FILA 2: Preguntas + Radar ========== */}
        <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
          <Col xs={24} lg={14}>
            <Card
              title={
                <Space style={{ width: '100%', justifyContent: 'space-between' }}>
                  <Space>
                    <RiseOutlined style={{ color: '#722ed1' }} />
                    <span>Valoración Media por Pregunta</span>
                    <Tag color="purple">{dataPorPregunta.length}</Tag>
                  </Space>
                  <Segmented
                    size="small"
                    value={tipoGraficoPreguntas}
                    onChange={setTipoGraficoPreguntas}
                    options={[
                      { label: 'Barras', value: 'bar' },
                      { label: 'Línea', value: 'line' },
                    ]}
                  />
                </Space>
              }
              size="small"
              style={{ borderRadius: 12 }}
              styles={{ body: { padding: isMobile ? 8 : 16 } }}
            >
              {dataPorPregunta.length > 0 ? (
                <ResponsiveContainer
                  width="100%"
                  height={Math.max(260, dataPorPregunta.length * 38 + 60)}
                >
                  {tipoGraficoPreguntas === 'bar' ? (
                    <BarChart data={dataPorPregunta} layout="vertical"
                      margin={{ top: 5, right: 30, left: 5, bottom: 5 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                      <XAxis type="number" domain={[0, 4]} tick={{ fontSize: 11 }} />
                      <YAxis dataKey="pregunta" type="category"
                        tick={{ fontSize: 11 }} width={isMobile ? 130 : 200} />
                      <RTooltip
                        contentStyle={{ borderRadius: 8, border: 'none',
                          boxShadow: '0 4px 12px rgba(0,0,0,0.15)' }}
                        formatter={(v, n, p) => [`${Number(v).toFixed(2)}/4`,
                          p.payload.preguntaFull]}
                      />
                      <Bar dataKey="media" radius={[0, 6, 6, 0]}
                        label={{ position: 'right', fontSize: 11 }}>
                        {dataPorPregunta.map((entry, i) => {
                          const v = entry.media;
                          const color = v >= 3.5 ? '#52c41a' : v >= 2.5 ? '#1890ff'
                            : v >= 1.5 ? '#faad14' : '#ff4d4f';
                          return <Cell key={i} fill={color} />;
                        })}
                      </Bar>
                    </BarChart>
                  ) : (
                    <LineChart data={dataPorPregunta}
                      margin={{ top: 20, right: 30, left: 5, bottom: 5 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                      <XAxis dataKey="pregunta" tick={{ fontSize: 10 }}
                        angle={-30} textAnchor="end" height={100} interval={0} />
                      <YAxis domain={[0, 4]} tick={{ fontSize: 11 }} />
                      <RTooltip
                        contentStyle={{ borderRadius: 8, border: 'none',
                          boxShadow: '0 4px 12px rgba(0,0,0,0.15)' }}
                        formatter={(v, n, p) => [`${Number(v).toFixed(2)}/4`,
                          p.payload.preguntaFull]}
                      />
                      <Line type="monotone" dataKey="media" stroke="#722ed1"
                        strokeWidth={2} dot={{ r: 4, fill: '#722ed1' }} />
                    </LineChart>
                  )}
                </ResponsiveContainer>
              ) : <Empty description="Sin datos" />}
            </Card>
          </Col>

          <Col xs={24} lg={10}>
            <Card
              title={<Space><ExperimentOutlined style={{ color: '#13c2c2' }} /> Radar de Categorías</Space>}
              size="small"
              style={{ borderRadius: 12 }}
              styles={{ body: { padding: isMobile ? 8 : 16 } }}
            >
              {dataPorCategoria.length > 0 ? (
                <ResponsiveContainer width="100%" height={isMobile ? 260 : 320}>
                  <RadarChart data={dataPorCategoria} outerRadius={isMobile ? 70 : 90}>
                    <PolarGrid stroke="#e6e6e6" />
                    <PolarAngleAxis dataKey="name" tick={{ fontSize: 11 }} />
                    <PolarRadiusAxis angle={90} domain={[0, 4]} tick={{ fontSize: 10 }} />
                    <Radar name="Media" dataKey="value"
                      stroke="#13c2c2" fill="#13c2c2" fillOpacity={0.5} />
                    <RTooltip
                      contentStyle={{ borderRadius: 8, border: 'none',
                        boxShadow: '0 4px 12px rgba(0,0,0,0.15)' }}
                      formatter={(v) => [`${Number(v).toFixed(2)}/4`, 'Media']}
                    />
                  </RadarChart>
                </ResponsiveContainer>
              ) : <Empty description="Sin datos" />}
            </Card>
          </Col>
        </Row>

        {/* ========== FILA 3: Países + Edades ========== */}
        <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
          <Col xs={24} lg={14}>
            <Card
              title={<Space><GlobalOutlined style={{ color: '#13c2c2' }} /> Top Países de Procedencia</Space>}
              size="small"
              style={{ borderRadius: 12 }}
              styles={{ body: { padding: isMobile ? 8 : 16 } }}
            >
              {dataPorPais.length > 0 ? (
                <ResponsiveContainer
                  width="100%"
                  height={Math.max(260, dataPorPais.length * 32 + 40)}
                >
                  <BarChart data={dataPorPais} layout="vertical"
                    margin={{ top: 5, right: 30, left: 5, bottom: 5 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                    <XAxis type="number" tick={{ fontSize: 11 }} />
                    <YAxis dataKey="name" type="category"
                      tick={{ fontSize: 11 }} width={isMobile ? 90 : 130} />
                    <RTooltip
                      contentStyle={{ borderRadius: 8, border: 'none',
                        boxShadow: '0 4px 12px rgba(0,0,0,0.15)' }}
                      formatter={(v) => [`${v} encuestas`, 'Cantidad']}
                    />
                    <Bar dataKey="value" radius={[0, 6, 6, 0]}
                      label={{ position: 'right', fontSize: 11 }}>
                      {dataPorPais.map((entry, i) => (
                        <Cell key={i} fill={COLORS[i % COLORS.length]} />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              ) : <Empty description="Sin datos" />}
            </Card>
          </Col>

          <Col xs={24} lg={10}>
            <Card
              title={<Space><TeamOutlined style={{ color: '#fa8c16' }} /> Distribución por Edad</Space>}
              size="small"
              style={{ borderRadius: 12 }}
              styles={{ body: { padding: isMobile ? 8 : 16 } }}
            >
              {dataPorEdad.length > 0 ? (
                <ResponsiveContainer width="100%" height={isMobile ? 260 : 320}>
                  <BarChart data={dataPorEdad} margin={{ top: 20, right: 20, left: 0, bottom: 5 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                    <XAxis dataKey="name" tick={{ fontSize: 11 }} />
                    <YAxis tick={{ fontSize: 11 }} />
                    <RTooltip
                      contentStyle={{ borderRadius: 8, border: 'none',
                        boxShadow: '0 4px 12px rgba(0,0,0,0.15)' }}
                      formatter={(v) => [`${v} encuestas`, 'Cantidad']}
                    />
                    <Bar dataKey="value" fill="#fa8c16" radius={[6, 6, 0, 0]}
                      label={{ position: 'top', fontSize: 11 }} />
                  </BarChart>
                </ResponsiveContainer>
              ) : <Empty description="Sin datos" />}
            </Card>
          </Col>
        </Row>

        {/* ========== TABLA PRINCIPAL ========== */}
        <Card
          title={
            <Space>
              <FileTextOutlined style={{ color: '#722ed1' }} />
              <span>Listado de encuestas</span>
              <Badge count={respuestas.length} style={{ backgroundColor: '#722ed1' }} />
            </Space>
          }
          size="small"
          style={{ borderRadius: 12 }}
          styles={{ body: { padding: isMobile ? 0 : 16 } }}
        >
          <Table
            columns={columns}
            dataSource={respuestas}
            rowKey="Id"
            loading={loading}
            scroll={{ x: isMobile ? 800 : 1400 }}
            size={isMobile ? 'small' : 'middle'}
            pagination={{
              pageSize: 10,
              showSizeChanger: !isMobile,
              showTotal: (t) => `${t} encuestas`,
            }}
            onChange={(pagination, filters, sorter) => {
              setFilteredInfo(filters);
              setSortedInfo(sorter);
            }}
            locale={{ emptyText: <Empty description="No se encontraron encuestas" /> }}
          />
        </Card>
      </PageContainer>

      {/* Modal de detalle */}
      <Modal
        title={`Detalle de encuesta #${detalle?.cabecera?.Id || ''}`}
        open={detalleVisible}
        onCancel={() => { setDetalleVisible(false); setDetalle(null); }}
        footer={[
          <Button key="close" type="primary"
            onClick={() => { setDetalleVisible(false); setDetalle(null); }}>
            Cerrar
          </Button>
        ]}
        width={800}
        style={{ top: 20 }}
        bodyStyle={{ maxHeight: 'calc(100vh - 200px)', overflowY: 'auto' }}
      >
        {loadingDetalle ? (
          <div style={{ textAlign: 'center', padding: 40 }}>Cargando...</div>
        ) : detalle ? (
          <>
            <Descriptions title="Datos del cliente" bordered size="small"
              column={{ xs: 1, sm: 2 }} style={{ marginBottom: 20 }}>
              <Descriptions.Item label="País">
                {detalle.cabecera.PaisProcedencia || '-'}
              </Descriptions.Item>
              <Descriptions.Item label="Habitación">
                {detalle.cabecera.NumHabitacion || '-'}
              </Descriptions.Item>
              <Descriptions.Item label="Edad">
                {detalle.cabecera.Edad || '-'}
              </Descriptions.Item>
              <Descriptions.Item label="Fecha encuesta">
                {detalle.cabecera.FechaEncuesta
                  ? dayjs(detalle.cabecera.FechaEncuesta).format('DD/MM/YYYY HH:mm')
                  : '-'}
              </Descriptions.Item>
              <Descriptions.Item label="Llegada">
                {detalle.cabecera.FechaEntrada
                  ? dayjs(detalle.cabecera.FechaEntrada).format('DD/MM/YYYY')
                  : '-'}
              </Descriptions.Item>
              <Descriptions.Item label="Salida">
                {detalle.cabecera.FechaSalida
                  ? dayjs(detalle.cabecera.FechaSalida).format('DD/MM/YYYY')
                  : '-'}
              </Descriptions.Item>
              <Descriptions.Item label="¿Ha estado antes?">
                {detalle.cabecera.HaEstadoAntes ? 'Sí' : 'No'}
              </Descriptions.Item>
              <Descriptions.Item label="¿Volvería?">
                {detalle.cabecera.LeGustariaVolver ? 'Sí' : 'No'}
              </Descriptions.Item>
            </Descriptions>

            <Text strong style={{ fontSize: 15 }}>Respuestas por categoría</Text>
            <div style={{ marginTop: 12 }}>
              {detalle.detalle.map(d => (
                <Card key={d.Id} size="small" style={{ marginBottom: 8 }}
                  title={<span style={{ fontSize: 13 }}>{d.CategoriaDescripcion}</span>}>
                  <div style={{ marginBottom: 4 }}>
                    <Text>{d.PreguntaTexto}</Text>
                  </div>
                  <Space>
                    <Tag color={valoracionColor(d.Valoracion)}
                      style={{ fontWeight: 'bold', fontSize: 13 }}>
                      {VALORACION_LABEL[d.Valoracion] || d.Valoracion}
                    </Tag>
                    {d.Comentario && (
                      <Text italic style={{ color: '#666', fontSize: 12 }}>
                        "{d.Comentario}"
                      </Text>
                    )}
                  </Space>
                </Card>
              ))}
            </div>

            {detalle.cabecera.Observaciones && (
              <Card size="small" title="Observaciones generales"
                style={{ marginTop: 16, backgroundColor: '#fffbe6' }}>
                <Text>{detalle.cabecera.Observaciones}</Text>
              </Card>
            )}
          </>
        ) : null}
      </Modal>
    </>
  );
};

export default EncuestasListado;